/**
 * StaffKnowledge — IT-SCE Intern: Aria-Wissensbasis-Übersicht (read-only MVP).
 *
 * TQ #7128. Backend GET /api/staff/knowledge (#7114, staff-auth) liest
 * cortex.knowledge_docs (enabled=true) und liefert {documents, total}.
 *
 * MVP-Scope (Patric-Entscheidung 2026-06-12, Resume #7128):
 *  - Read-only-Tabelle der indexierten Dokumente.
 *  - Reindex-Button DISABLED: cortex hat (noch) keinen reindex-HTTP-Endpoint;
 *    A/B-Entscheidung (cortex-Route vs portal-api-Workaround) offen → Folge-Task.
 *  - Erste reale /staff/*-Page in org-portal (Unified-Cockpit-Migration #6919).
 *
 * Routing: outer-router-level (App.tsx) vor /:slug/*, hinter StaffOnly-Guard.
 * Eigener schlanker Shell (kein CockpitApp-Layout) bis #6919 die Staff-Pages
 * in den Cockpit-Shell zieht.
 */

import { useEffect, useState } from 'react'
import { api, StaffKnowledgeDoc } from '../services/api'

function formatDate(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleString('de-DE', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

export default function StaffKnowledge() {
  const [docs, setDocs] = useState<StaffKnowledgeDoc[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    setLoading(true)
    setError(null)
    api
      .getStaffKnowledge()
      .then((resp) => {
        setDocs(resp.documents)
        setTotal(resp.total)
      })
      .catch((err: unknown) => {
        console.error('[StaffKnowledge] load failed:', err)
        setError('Wissensbasis konnte nicht geladen werden.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <a href="/" style={{ color: '#FF6B35', textDecoration: 'none', fontSize: '0.9rem' }}>
          ← Cockpit
        </a>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.6rem' }}>
          📚 Aria-Wissensbasis
        </h1>
        <button
          type="button"
          disabled
          title="Reindex-Endpoint noch nicht verfügbar (TQ #7128, Folge-Task)"
          style={{
            padding: '0.5rem 1rem', borderRadius: 6, border: '1px solid #ccc',
            background: '#f3f3f3', color: '#999', cursor: 'not-allowed', fontSize: '0.9rem',
          }}
        >
          ⟳ Reindex (bald)
        </button>
      </div>
      <p style={{ color: '#666', marginTop: '0.5rem', fontSize: '0.9rem' }}>
        Indexierte Dokumente der Aria-Wissensbasis (read-only). {total > 0 && `${total} Dokument(e).`}
      </p>

      {loading && <p style={{ color: '#666' }}>Lade Wissensbasis…</p>}

      {error && (
        <div style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#b91c1c' }}>
          {error}{' '}
          <button onClick={load} style={{ marginLeft: 8, textDecoration: 'underline', background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer' }}>
            Erneut versuchen
          </button>
        </div>
      )}

      {!loading && !error && docs.length === 0 && (
        <p style={{ color: '#666' }}>Keine indexierten Dokumente gefunden.</p>
      )}

      {!loading && !error && docs.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '0.6rem 0.75rem' }}>Titel</th>
                <th style={{ padding: '0.6rem 0.75rem' }}>Tenant</th>
                <th style={{ padding: '0.6rem 0.75rem' }}>Scope</th>
                <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Chunks</th>
                <th style={{ padding: '0.6rem 0.75rem' }}>Aktualisiert</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((doc, i) => (
                <tr key={doc.id ?? `${doc.source_path}-${i}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '0.6rem 0.75rem' }}>
                    <div style={{ fontWeight: 500 }}>{doc.title || '—'}</div>
                    {doc.source_path && (
                      <div style={{ color: '#999', fontSize: '0.8rem' }}>{doc.source_path}</div>
                    )}
                  </td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>{doc.tenant_id || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>
                    {Array.isArray(doc.scope) && doc.scope.length > 0 ? doc.scope.join(', ') : '—'}
                  </td>
                  <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{doc.chunk_count ?? 0}</td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>{formatDate(doc.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
